import { useQuery } from "@apollo/client";
import { Col, Empty, Form, Row, Table } from "antd";
import Search from "antd/lib/input/Search";
import { PaginationConfig } from "antd/lib/pagination";
import moment from "moment";
import React, { useState } from "react";
import Error from "../../components/apollo_error";
import DateRange from "../../components/date_range";
import LoadingSpinner from "../../components/loading_indicator";
import {
  GENERATE_STOCK_SUMMARY,
  getGenerateStockSummaryVariables,
  get_total_product_stock_items_count,
} from "../../graphql/inventory";
import withClinic from "../../hooks/with_clinic";
import { ApolloResult, Clinic } from "../../store";

interface P {
  currentClinic?: Clinic;
}

interface StockSummaryItem {
  id: string;
  name: string;
  opening_qty: number;
  in_qty: number;
  out_qty: number;
  closing_qty: number;
}

const StockSummaryPage = (props: P) => {
  const clinicId = props.currentClinic?.id || "";
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [pagination, setPagination] = useState<PaginationConfig | null>(null);
  const [ranges, setRanges] = useState({
    startDate: moment().subtract(30, 'days').utc().startOf('day'),
    endDate: moment().utc().startOf('day'),
  });

  const take = pagination?.pageSize || 20;
  const skip = ((pagination?.current || 1) - 1) * take;

  const fromDate = ranges.startDate.utc().startOf('day').toDate();
  const toDate = ranges.endDate.utc().startOf('day').toDate();
  
  const { variables } = getGenerateStockSummaryVariables({
    clinicId,
    take,
    skip,
    searchText,
    fromDate: fromDate,
    toDate: toDate,
  });

  const { loading, data, error } = useQuery<
    ApolloResult<"generateStockSummaryReport", StockSummaryItem[]>
  >(GENERATE_STOCK_SUMMARY, { variables });

  // Get total count for pagination
  const { variables: countVariables } = getGenerateStockSummaryVariables({
    clinicId,
    take: 1,
    skip: 0,
    searchText,
    fromDate: fromDate,
    toDate: toDate,
  });

  const totalCount = useQuery<
    ApolloResult<"aggregateProductStockItem", { _count: { id: number } }>
  >(get_total_product_stock_items_count, { variables: countVariables });

  if (loading || totalCount.loading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (totalCount.error) return <Error error={totalCount.error} />;
  if (!data) return <Empty />;

  const onDateChange = (startDate: Date, endDate: Date) => {
    if (startDate && endDate) {
      setRanges({ 
        startDate: moment(startDate).utc().startOf('day'),
        endDate: moment(endDate).utc().startOf('day')
      });
    }
  };

  const getStockStatusColor = (closingQty: number) => {
    if (closingQty <= 0) return "red";
    if (closingQty <= 10) return "orange";
    return "green";
  };

  const getStockStatusText = (closingQty: number) => {
    if (closingQty <= 0) return "Out of Stock";
    if (closingQty <= 10) return "Low Stock";
    return "In Stock";
  };

  const columns: any[] = [
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      editable: false,
      width: 250,
      ellipsis: true,
      sorter: (a: StockSummaryItem, b: StockSummaryItem) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    },
    {
      title: "Opening Qty",
      dataIndex: "opening_qty",
      key: "opening_qty",
      editable: false,
      width: 120,
      sorter: (a: StockSummaryItem, b: StockSummaryItem) => a.opening_qty - b.opening_qty,
      render: (v: number, r: StockSummaryItem) => {
        return (
          <span style={{ fontWeight: 500, color: "#1890ff" }}>
            {r.opening_qty}
          </span>
        );
      },
    },
    {
      title: "In Qty",
      dataIndex: "in_qty",
      key: "in_qty",
      width: 100,
      sorter: (a: StockSummaryItem, b: StockSummaryItem) => a.in_qty - b.in_qty,
      editable: false,
      render: (v: number, r: StockSummaryItem) => {
        return (
          <span style={{ color: "green", fontWeight: 500 }}>
            {r.in_qty}
          </span>
        );
      },
    },
    {
      title: "Out Qty",
      dataIndex: "out_qty",
      key: "out_qty",
      width: 100,
      sorter: (a: StockSummaryItem, b: StockSummaryItem) => a.out_qty - b.out_qty,
      editable: false,
      render: (v: number, r: StockSummaryItem) => {
        return (
          <span style={{ color: "red", fontWeight: 500 }}>
            {r.out_qty}
          </span>
        );
      },
    },
    {
      title: "Closing Qty",
      dataIndex: "closing_qty",
      key: "closing_qty",
      width: 120,
      sorter: (a: StockSummaryItem, b: StockSummaryItem) => a.closing_qty - b.closing_qty,
      editable: false,
      render: (v: number, r: StockSummaryItem) => {
        return (
          <span style={{ fontWeight: 600, color: getStockStatusColor(r.closing_qty) }}>
            {r.closing_qty}
          </span>
        );
      },
    },

  ];

  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>Stock Summary</Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText || undefined}
              placeholder="Search product names..."
              allowClear
              size="middle"
              onSearch={(val: any) => setSearchText(val)}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate.toDate()}
          endDate={ranges.endDate.toDate()}
          onSelect={onDateChange}
        />
      </Col>
    </Row>
  );

  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form component={false}>
          <Table
            showHeader={data.generateStockSummaryReport.length > 0}
            dataSource={data.generateStockSummaryReport.map((b) => ({ key: b.id, ...b }))}
            columns={columns}
            rowClassName="editable-row"
            onChange={(p: PaginationConfig) => {
              setPagination(p);
            }}
            pagination={{
              total: totalCount.data?.aggregateProductStockItem._count.id || 0,
              current: pagination?.current,
              pageSize: pagination?.pageSize,
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            scroll={{ x: 800 }}
          />
        </Form>
      </Col>
    </Row>
  );

  return (
    <React.Fragment>
      {control}
      {context}
    </React.Fragment>
  );
};

export default withClinic(StockSummaryPage);
